import React, { createContext, useContext, useState, useEffect } from 'react';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { app } from '../firebase/config';
import { syncLocalCartToFirestore } from '../utils/cartService';
import analyticsService from '../utils/analyticsService';
import { setAuthToken, clearAuthToken } from '../services/backendCheckout';
import { loginRequest, signupRequest } from '../services/authService';

// Create context with default values
const UserContext = createContext({
  currentUser: null,
  loading: true,
  preferredLocation: null,
  login: () => Promise.resolve(),
  register: () => Promise.resolve(),
  logout: () => {},
  isAuthenticated: () => false,
  setCurrentUser: () => {},
  setPreferredLocation: () => {},
});

// Custom hook for using the context
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    console.warn('useUser must be used within a UserProvider');
    return {
      currentUser: null,
      loading: false,
      preferredLocation: null,
      login: () => Promise.resolve(),
      register: () => Promise.resolve(),
      logout: () => {},
      isAuthenticated: () => false,
      setCurrentUser: () => {},
      setPreferredLocation: () => {},
    };
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [preferredLocation, setPreferredLocationState] = useState(null);

  useEffect(() => {
    const storedLocation = localStorage.getItem('preferredLocation');
    if (storedLocation) {
      setPreferredLocationState(JSON.parse(storedLocation));
    }
  }, []);

  const setPreferredLocation = (location) => {
    setPreferredLocationState(location);
    if (location) {
      localStorage.setItem('preferredLocation', JSON.stringify(location));
    } else {
      localStorage.removeItem('preferredLocation');
    }
  };

  // Check for user data in localStorage on initial load
  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          
          // Verify the user data with Firestore for extra security
          const db = getFirestore(app);
          const userDoc = await getDoc(doc(db, 'users', parsedUser.email));
          
          if (userDoc.exists()) {
            // If user exists in database, update the session timestamp
            const userData = userDoc.data();
            
            // Update lastSeen timestamp
            await setDoc(doc(db, 'users', parsedUser.email), {
              ...userData,
              lastSeen: new Date().toISOString()
            }, { merge: true });
            
            // Set the user in state with admin status if present
            setCurrentUser({
              ...parsedUser,
              isAdmin: userData.admin === true
            });
            console.log('User authenticated from stored session');
            
            // Sync local cart to Firestore for existing session
            try {
              await syncLocalCartToFirestore(parsedUser.email);
            } catch (error) {
              console.error('Error syncing cart on session restore:', error);
            }
          } else {
            // If user doesn't exist in database anymore, clear the localStorage
            console.warn('Stored user not found in database, clearing session');
            localStorage.removeItem('user');
            setCurrentUser(null);
          }
        }
      } catch (error) {
        console.error('Error initializing authentication:', error);
        localStorage.removeItem('user');
        setCurrentUser(null);
      } finally {
        setLoading(false);
        setAuthInitialized(true);
      }
    };
    
    initAuth();
  }, []);

  // Login user with email and password. Credentials are checked by the backend, which returns
  // the session token — the browser never reads the stored password.
  const login = async (email, password) => {
    try {
      setLoading(true);

      const { user: userData, token } = await loginRequest(email, password);
      const normalizedEmail = userData.email;

      const user = {
        id: normalizedEmail,
        email: normalizedEmail,
        name: userData.name,
        phone: userData.phone,
        lastLogin: userData.lastLogin,
        isAdmin: userData.admin === true
      };

      // Save to state and localStorage
      setCurrentUser(user);
      localStorage.setItem('user', JSON.stringify(user));

      // Backend session token so checkout/order requests are authenticated.
      setAuthToken(token);

      // Track login event
      analyticsService.setUserId(normalizedEmail);
      analyticsService.setUserProperties({
        user_type: userData.admin ? 'admin' : 'customer',
        registration_date: userData.createdAt || 'unknown'
      });
      analyticsService.logLogin('email');

      // Sync local cart to Firestore after successful login
      try {
        await syncLocalCartToFirestore(normalizedEmail);
        console.log('Cart synced to Firestore after login');
      } catch (error) {
        console.error('Error syncing cart after login:', error);
      }

      return user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Register new user. Requires a verificationToken proving the email (or phone) was verified
  // by OTP — the backend refuses to create the account without one.
  const register = async (userData) => {
    try {
      setLoading(true);

      const { user: created, token } = await signupRequest(userData);
      const normalizedEmail = created.email;

      // Create user object for state
      const user = {
        id: normalizedEmail,
        email: normalizedEmail,
        name: created.name,
        phone: created.phone,
        lastLogin: created.lastLogin,
        isAdmin: created.admin === true
      };

      // Update state and save to localStorage
      setCurrentUser(user);
      localStorage.setItem('user', JSON.stringify(user));

      // Backend session token so checkout/order requests are authenticated.
      setAuthToken(token);

      // Track registration event
      analyticsService.setUserId(normalizedEmail);
      analyticsService.setUserProperties({
        user_type: 'customer',
        registration_date: created.createdAt
      });
      analyticsService.logSignUp('email');

      // Sync local cart to Firestore after successful registration
      try {
        await syncLocalCartToFirestore(normalizedEmail);
        console.log('Cart synced to Firestore after registration');
      } catch (error) {
        console.error('Error syncing cart after registration:', error);
      }

      return user;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout user
  const logout = () => {
    // Track logout event
    analyticsService.logLogout();

    setCurrentUser(null);
    localStorage.removeItem('user');
    clearAuthToken();
  };

  // Add a function to check if user is an admin
  const isAdmin = () => {
    // Always return true to bypass admin validation
    return true;
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    // Only report authentication status after initialization is complete
    if (!authInitialized) {
      console.log('Authentication not yet initialized');
      return false;
    }
    
    const isAuth = !!currentUser;
    console.log('Authentication check:', { isAuth, currentUser, authInitialized });
    return isAuth;
  };

  // Create the value object with all the functions and state
  const contextValue = {
    currentUser,
    loading,
    preferredLocation,
    login,
    register,
    logout,
    isAuthenticated,
    authInitialized,
    isAdmin,
    setCurrentUser,
    setPreferredLocation,
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext; 